import { useEffect } from 'react';

import { create } from 'zustand';

import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';
import { DEMO_TENANT_NAME } from 'src/common/services/api/demoTenantGraphqlMocks';
import { Logger } from 'src/common/services/Logger';

import { processPlatformConfiguration } from '../helpers/ConfigurationProcessingHelpers';
import { sanitizeConfigurationLabel } from '../helpers/DataSanitizationHelpers';
import { BusinessUnit } from '../types/DashboardTypes';

interface IDashboardConfigStore {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;

	// Actions
	setBusinessUnits: (units: BusinessUnit[]) => void;
	clearBusinessUnits: () => void;

	// Selectors
	getBusinessUnitById: (id: string) => BusinessUnit | null;
	getFirstBusinessUnit: () => BusinessUnit | null;
}

const useDashboardConfigStore = create<IDashboardConfigStore>((set, get) => ({
	businessUnits: [],
	isLoading: false,
	error: null,

	setBusinessUnits: (units: BusinessUnit[]) =>
		set({
			businessUnits: units,
			isLoading: false,
			error: null
		}),

	clearBusinessUnits: () =>
		set({
			businessUnits: [],
			isLoading: false,
			error: null
		}),

	getBusinessUnitById: (id: string) => {
		const { businessUnits } = get();
		return businessUnits.find((unit) => unit.id === id) || null;
	},

	getFirstBusinessUnit: () => {
		const { businessUnits } = get();
		return businessUnits.length > 0 ? businessUnits[0] : null;
	}
}));

export const useBusinessUnitsFromConfig = (): {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;
} => {
	const { configuration } = useConfiguration();
	const { tenant } = useUserTenant();
	const { businessUnits, setBusinessUnits, error } = useDashboardConfigStore();

	// Extract business units from configuration when it's available
	useEffect(() => {
		Logger.info('useBusinessUnitsFromConfig effect running', {
			tenantName: tenant?.name,
			isDemoTenant: tenant?.name === DEMO_TENANT_NAME,
			DEMO_TENANT_NAME,
			businessUnitsCount: businessUnits.length,
			hasConfiguration: !!configuration
		});

		// Priority 1: Demo tenant - always use mock business units
		if (tenant?.name === DEMO_TENANT_NAME) {
			// Check if we already have the demo units loaded
			const hasDemoUnits =
				businessUnits.length > 0 &&
				businessUnits.some((u) => ['Business accounts', 'Credit cards', 'Loans'].includes(u.id));

			Logger.info('Demo tenant detected', {
				hasDemoUnits,
				businessUnitsCount: businessUnits.length
			});

			if (!hasDemoUnits) {
				// Dynamically load demo tenant mocks
				const loadDemoUnits = async (): Promise<void> => {
					try {
						// @ts-expect-error - Dynamic imports work with webpack despite tsconfig module setting
						const module = await import('src/common/services/api/demoTenantGraphqlMocks.json');
						const demoUnits = (module.default.BusinessUnits ?? []).map((value: string) => ({
							id: value,
							name: value
						}));
						Logger.info('Setting demo tenant business units', { demoUnits });
						setBusinessUnits(demoUnits);
					} catch (error) {
						Logger.error('Failed to load demo tenant business units:', error);
						setBusinessUnits([]);
					}
				};
				loadDemoUnits();
			}
			return; // IMPORTANT: Don't load from config for demo tenant
		}

		// Priority 2: Regular configuration (only for non-demo tenants)
		if (!configuration) {
			return;
		}

		if (businessUnits.length > 0) {
			return;
		}

		if (!configuration.PLATFORM) {
			Logger.warn('No PLATFORM configuration found');
			setBusinessUnits([]);
			return;
		}

		try {
			const { coreLabels } = processPlatformConfiguration(configuration.PLATFORM);
			const businessUnitLabel = coreLabels.find((label) => label.label === 'unitId');

			if (!businessUnitLabel) {
				Logger.warn('No unitId label found in configuration');
				setBusinessUnits([]);
				return;
			}

			const sanitized = sanitizeConfigurationLabel(businessUnitLabel);

			if (sanitized.values.length > 0) {
				const units = sanitized.values.map((value) => ({
					id: value,
					name: value
				}));
				setBusinessUnits(units);
			} else {
				setBusinessUnits([]);
			}
		} catch (error) {
			Logger.error('Failed to extract business units from configuration:', error);
			setBusinessUnits([]);
		}
	}, [tenant?.name, configuration, businessUnits.length, setBusinessUnits, businessUnits]);

	const actualIsLoading = !configuration && businessUnits.length === 0;

	return {
		businessUnits,
		isLoading: actualIsLoading,
		error
	};
};
